import Box from '@mui/material/Box';
import { FormattedMessage, useIntl } from 'react-intl';
import Typography from '@mui/material/Typography';
import { ReactNode } from 'react';
import {
  Feature,
  getAddOnCodeByFeature,
  getFeatureFromPlan,
  Order,
  OrderItem,
  SubscriptionSource,
} from '../../../../models';
import { CurrencyDisplay, formatCurrency } from '../../../../components';
import { DEFAULT_CURRENCY_FORMAT_OPTIONS, USD_CURRENCY } from '../../../../utils/Constants';
import { BillingCycle } from '../../../subscription/component/BillingCycle';

export const OrderDetails = ({ order }: { order: Order }) => {
  const intl = useIntl();
  const discountAmount = order.orderItems
    .reduce((sum, { discountAmount }) => sum + discountAmount, 0);

  const renderHeaderCell = (title: string, width?: number) => (
    <Box
      key={`order-details-table-${title}-header`}
      sx={{ width: width ?? 200 }}
    >
      <Typography fontSize={15} fontWeight={700} lineHeight="180%" color="#FFFFFF" paddingLeft={2}>
        <FormattedMessage id={title} />
      </Typography>
    </Box>
  );

  const renderHeaderRow = () => (
    <Box
      key="order-details-table-header"
      id="order-details-table-header"
      display="flex"
      flexDirection="row"
      p={2}
      sx={{
        backgroundColor: '#5959EB',
      }}
    >
      {renderHeaderCell('page.order.history.order.item.product.title', 400)}
      {renderHeaderCell('page.order.history.order.item.billing.cycle.title', 200)}
      {renderHeaderCell('page.order.history.order.item.quantity.title', 150)}
      {renderHeaderCell('page.order.history.order.item.unit.price.title', 150)}
      {renderHeaderCell('page.order.history.order.item.extended.price.title')}
    </Box>
  );

  const getProductName = (item: OrderItem) => {
    let productName;

    const feature = getFeatureFromPlan(item.planId);
    if (feature) {
      productName = intl.formatMessage({ id: `feature.${feature.toLowerCase()}.product.name` });
    }

    // for purchase order items with type are stater package,
    // show the package name in the product name
    if (item.type === 'STARTER_PACKAGE' && item.origin === 'PURCHASE') {
      const prefix = `${intl.formatMessage({ id: `promotion.sp.${item.productName.toLowerCase()}.name` })}`;
      return `${prefix} - ${productName}`;
    }
    return productName ?? item.productName;
  };

  const renderItemCell = (key: string, value: any, width?: number) => (
    <Box
      key={`order-details-table-${key}`}
      sx={{ width: width ?? 200 }}
    >
      <Typography fontSize={14} fontWeight={500} lineHeight="180%" paddingLeft={2}>
        {value}
      </Typography>
    </Box>
  );

  const isLegacyTruePlatformOrderItem = (item: OrderItem) => {
    const feature = getFeatureFromPlan(item.planId);
    return feature === Feature.TRUE_TEAM;
  };

  const getAddonDisplayName = (item: OrderItem) => {
    const feature = getFeatureFromPlan(item.planId);
    const addOnCode = feature ? getAddOnCodeByFeature(feature) : undefined;
    return addOnCode
      ? intl.formatMessage({ id: `addon.${addOnCode}.display.name` })
      : getProductName(item);
  };

  const renderNormalItemRow = (item: OrderItem) => {
    const productName = getProductName(item);
    return (
      <Box
        key={`order-details-table-item-${item.id}`}
        id={`order-details-table-item-${item.id}`}
        display="flex"
        flexDirection="row"
        paddingX={2}
        paddingY={1.25}
        alignContent="center"
        alignItems="center"
        borderTop="1px solid #D6D6D6"
      >
        {renderItemCell('product-name', productName, 400)}
        {renderItemCell(
          'billing-interval',
          (
            <BillingCycle
              source={SubscriptionSource.RECURLY}
              billingInterval={item.billingCycle}
              position="flex-start"
              padding={{ pt: 0, pr: 0, pb: 0, pl: 0 }}
              useChip
              shortName
            />
          ),
          200,
        )}
        {renderItemCell('quantity', item.quantity, 150)}
        {renderItemCell(
          'unit-amount',
          (
            <CurrencyDisplay
              amount={item.unitAmount}
              currency={USD_CURRENCY}
            />
          ),
          150,
        )}
        {renderItemCell(
          'total-amount',
          (
            <CurrencyDisplay
              amount={item.totalAmount}
              currency={USD_CURRENCY}
            />
          ),
        )}
      </Box>
    );
  };

  const renderTruePlatformRow = (item: OrderItem, addonItems: OrderItem[]) => {
    const productName = getProductName(item);
    return (
      <Box
        key={`order-details-table-item-${item.id}`}
        id={`order-details-table-item-${item.id}`}
        display="flex"
        flexDirection="row"
        px={2}
        pt={2}
        pb={addonItems.length > 0 ? 0 : 2}
        alignContent="center"
        alignItems="center"
        borderTop="1px solid #D6D6D6"
      >
        <Box sx={{ width: 400 }}>
          <Typography fontSize={14} fontWeight={700} lineHeight="180%" paddingLeft={2}>
            {productName}
          </Typography>
        </Box>
        {renderItemCell(
          'billing-interval',
          (
            <BillingCycle
              source={SubscriptionSource.RECURLY}
              billingInterval={item.billingCycle}
              position="flex-start"
              padding={{ pt: 0, pr: 0, pb: 0, pl: 0 }}
              useChip
              shortName
            />
          ),
          200,
        )}
        {renderItemCell('quantity', item.quantity, 150)}
        {renderItemCell(
          'unit-amount',
          (
            <CurrencyDisplay
              amount={item.unitAmount}
              currency={USD_CURRENCY}
            />
          ),
          150,
        )}
        {renderItemCell(
          'total-amount',
          (
            <CurrencyDisplay
              amount={item.totalAmount}
              currency={USD_CURRENCY}
            />
          ),
          200,
        )}
      </Box>
    );
  };

  const renderAddonItemRow = (item: OrderItem) => (
    <Box
      key={`order-details-table-item-${item.id}`}
      id={`order-details-table-item-${item.id}`}
      display="flex"
      flexDirection="row"
      alignContent="center"
      alignItems="center"
      px={2}
    >
      <Box
        sx={{
          width: 400,
          height: '100%',
        }}
      >
        <Typography
          borderLeft="2px solid #D6D6D6"
          fontSize={13}
          fontWeight={400}
          lineHeight="300%"
          color="#5C5C5C"
          ml={4}
          pl={2}
        >
          {getAddonDisplayName(item)}
        </Typography>
      </Box>
      {/* Empty billing interval column – addon items don't show a billing interval */}
      <Box sx={{ width: 200 }} />
      <Box sx={{ width: 150 }}>
        <Typography fontSize={13} fontWeight={400} lineHeight="100%" color="#5C5C5C" paddingLeft={2}>
          {item.quantity}
        </Typography>
      </Box>
      <Box sx={{ width: 150 }}>
        <Typography fontSize={13} fontWeight={400} lineHeight="100%" color="#5C5C5C" paddingLeft={2}>
          <CurrencyDisplay amount={item.unitAmount} currency={USD_CURRENCY} />
        </Typography>
      </Box>
      <Box sx={{ width: 200 }}>
        <Typography fontSize={13} fontWeight={400} lineHeight="100%" color="#5C5C5C" paddingLeft={2}>
          <CurrencyDisplay amount={item.totalAmount} currency={USD_CURRENCY} />
        </Typography>
      </Box>
    </Box>
  );

  const renderOrderItemRows = () => {
    const truePlatformItem = order.orderItems.find(isLegacyTruePlatformOrderItem);

    if (truePlatformItem) {
      const addonItems = order.orderItems.filter(item => item.id !== truePlatformItem.id);
      return [
        renderTruePlatformRow(truePlatformItem, addonItems),
        ...addonItems.map(renderAddonItemRow),
      ];
    }

    return order.orderItems.map(renderNormalItemRow);
  };

  const renderOrderSummarizeRow = (
    id: string,
    value: ReactNode,
    weight?: number,
    color?: string,
  ) => (
    <Box
      key={`order-details-table-summarize-${id}`}
      id={`order-details-table-summarize-${id}`}
      display="flex"
      flexDirection="row"
      justifyContent="flex-end"
    >
      <Box paddingRight={1}>
        <Typography
          fontSize={14}
          fontWeight={weight ?? 500}
          lineHeight="180%"
          paddingRight={2}
          color={color ?? '#000000'}
        >
          <FormattedMessage id={id} />
        </Typography>
      </Box>
      <Box width={130} paddingLeft={1}>
        <Typography
          fontSize={14}
          fontWeight={weight ?? 500}
          lineHeight="180%"
          paddingRight={2}
          color={color ?? '#000000'}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );

  const renderOrderSummarizeRows = () => (
    <Box
      key="order-details-table-summarize`"
      id="order-details-table-summarize"
      display="flex"
      flexDirection="column"
      mt={4}
      padding={theme => theme.spacing(3, 2, 3, 2)}
      borderTop="1px solid #D6D6D6"
    >
      {discountAmount > 0 && renderOrderSummarizeRow(
        'page.order.history.order.item.discount.title',
        (
          <FormattedMessage
            id="page.order.history.order.item.discount.value"
            values={{
              discountAmount: formatCurrency(intl, discountAmount, DEFAULT_CURRENCY_FORMAT_OPTIONS),
            }}
          />
        ),
      )}
      {renderOrderSummarizeRow(
        'page.order.history.order.item.grand.total.title',
        (
          <FormattedMessage
            id="page.order.history.order.item.grand.total.value"
            values={{
              amount: formatCurrency(
                intl,
                (order.totalAmount - order.taxAmount),
                DEFAULT_CURRENCY_FORMAT_OPTIONS,
              ),
            }}
          />
        ),
      )}
      {renderOrderSummarizeRow(
        'page.order.history.order.item.tax.title',
        (
          <FormattedMessage
            id="page.order.history.order.item.tax.value"
            values={{
              amount: formatCurrency(intl, order.taxAmount, DEFAULT_CURRENCY_FORMAT_OPTIONS),
            }}
          />
        ),
      )}
      {renderOrderSummarizeRow(
        'page.order.history.order.item.total.title',
        (
          <FormattedMessage
            id="page.order.history.order.item.total.value"
            values={{
              amount: formatCurrency(intl, order.totalAmount, DEFAULT_CURRENCY_FORMAT_OPTIONS),
            }}
          />
        ),
        700,
        '#5959EB',
      )}
    </Box>
  );

  return (
    <Box
      id="order-details-container"
      display="flex"
      flexDirection="column"
      border="1px solid #D6D6D6"
      borderRadius={0}
    >
      {/* Order header row */}
      {renderHeaderRow()}
      {/* Order item rows */}
      {renderOrderItemRows()}
      {/* Order summarize rows */}
      {renderOrderSummarizeRows()}
    </Box>
  );
};
